import { useEffect } from 'react';

const DEFAULT_SITE_NAME = 'SenritsuX';
const BASE_URL = 'https://senritsux.in';
const DEFAULT_TITLE = 'SenritsuX | The Artist-First Ecosystem - Music Distribution & Beats';
const DEFAULT_DESCRIPTION = 'SenritsuX empowers independent artists with global music distribution, verified beat marketplace, automated royalties, and 100% master ownership retention.';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;

function setOrUpdateMeta(attributeName, attributeValue, contentValue) {
  let element = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attributeName, attributeValue);
    document.head.appendChild(element);
  }
  element.setAttribute('content', contentValue || '');
  return element;
}

function setOrUpdateLink(rel, href) {
  let element = document.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
  return element;
}

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  noindex = false,
  ogType = 'website',
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt = 'SenritsuX - The Artist-First Music Ecosystem',
  jsonLd = null,
}) {
  useEffect(() => {
    // 1. Page Title
    const finalTitle = title
      ? title.includes(DEFAULT_SITE_NAME)
        ? title
        : `${title} | ${DEFAULT_SITE_NAME}`
      : DEFAULT_TITLE;
    document.title = finalTitle;

    // 2. Meta Description
    const finalDescription = description || DEFAULT_DESCRIPTION;
    setOrUpdateMeta('name', 'description', finalDescription);

    // 3. Keywords
    if (keywords) {
      const kw = Array.isArray(keywords) ? keywords.join(', ') : keywords;
      setOrUpdateMeta('name', 'keywords', kw);
    }

    // 4. Robots & Googlebot
    const robotsContent = noindex
      ? 'noindex, nofollow, noarchive'
      : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
    setOrUpdateMeta('name', 'robots', robotsContent);
    setOrUpdateMeta('name', 'googlebot', robotsContent);

    // 5. Canonical Link
    const rawCanonical = canonical || window.location.pathname;
    const finalCanonical = rawCanonical.startsWith('http')
      ? rawCanonical
      : `${BASE_URL}${rawCanonical.startsWith('/') ? '' : '/'}${rawCanonical}`;
    setOrUpdateLink('canonical', finalCanonical);

    // 6. Open Graph Tags
    const fullOgImage = ogImage.startsWith('http')
      ? ogImage
      : `${BASE_URL}${ogImage.startsWith('/') ? '' : '/'}${ogImage}`;

    setOrUpdateMeta('property', 'og:site_name', DEFAULT_SITE_NAME);
    setOrUpdateMeta('property', 'og:type', ogType);
    setOrUpdateMeta('property', 'og:title', finalTitle);
    setOrUpdateMeta('property', 'og:description', finalDescription);
    setOrUpdateMeta('property', 'og:url', finalCanonical);
    setOrUpdateMeta('property', 'og:image', fullOgImage);
    setOrUpdateMeta('property', 'og:image:alt', ogImageAlt);

    // 7. Twitter Tags
    setOrUpdateMeta('name', 'twitter:card', 'summary_large_image');
    setOrUpdateMeta('name', 'twitter:title', finalTitle);
    setOrUpdateMeta('name', 'twitter:description', finalDescription);
    setOrUpdateMeta('name', 'twitter:image', fullOgImage);

    // 8. Structured Data (JSON-LD)
    let scriptElement = null;
    const scriptId = 'senritsux-jsonld';
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }

    if (jsonLd) {
      scriptElement = document.createElement('script');
      scriptElement.id = scriptId;
      scriptElement.type = 'application/ld+json';
      scriptElement.text = JSON.stringify(jsonLd);
      document.head.appendChild(scriptElement);
    }

    // Cleanup on unmount or prop change
    return () => {
      if (scriptElement && document.head.contains(scriptElement)) {
        scriptElement.remove();
      }
    };
  }, [title, description, keywords, canonical, noindex, ogType, ogImage, ogImageAlt, jsonLd]);

  return null;
}
